import { useParams } from 'react-router-dom'
import { useEffect, useState } from 'react'
import ReelViewer from '../components/ReelViewer/ReelViewer'
import { getSectionWithCategories, getProjectsBySection } from '../services/sanity'
import CategoryFilter from '../components/CategoryFilter/CategoryFilter'
import type { Category, Project, Section } from '../types'

export default function SectionPage() {
  const { slug } = useParams<{ slug: string }>()
  const [section, setSection] = useState<Section & { categories?: Category[] } | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([])
  const [activeCategory, setActiveCategory] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function fetchData() {
      if (!slug) return

      try {
        const [sectionData, projectsData] = await Promise.all([
          getSectionWithCategories(slug),
          getProjectsBySection(slug)
        ])

        setSection(sectionData)
        setProjects(projectsData)
        setFilteredProjects(projectsData)
      } catch (error) {
        console.error('Error fetching section data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [slug])

  const handleCategoryFilter = (categorySlug: string | null) => {
    setActiveCategory(categorySlug)

    if (categorySlug === null) {
      setFilteredProjects(projects)
    } else {
      setFilteredProjects(
        projects.filter(p => p.category?.slug.current === categorySlug)
      )
    }
  }

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="text-white text-2xl">Loading projects...</div>
      </div>
    )
  }

  if (!section) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="text-white text-2xl">Section not found</div>
      </div>
    )
  }

  const hasCategories = section.categories && section.categories.length > 0

  return (
    <main className="min-h-screen">
      {/* Category Filter (if categories exist) */}
      {hasCategories && (
        <CategoryFilter
          categories={section.categories!}
          activeCategory={activeCategory}
          onSelectCategory={handleCategoryFilter}
        />
      )}

      {/* Reel Viewer with filtered projects */}
      {filteredProjects.length > 0 ? (
        <ReelViewer
          projects={filteredProjects}
          showCategoryFilter={hasCategories}
        />
      ) : (
        <div className="h-screen flex items-center justify-center bg-black">
          <div className="text-center text-white">
            <div className="text-4xl mb-4">Noch keine Projekte</div>
            <div className="text-apple-gray-400">
              {activeCategory
                ? 'In dieser Kategorie gibt es noch keine Projekte'
                : 'In diesem Bereich gibt es noch keine Projekte'}
            </div>
          </div>
        </div>
      )}
    </main>
  )
}
